<!-- Import and data: uploading broker files, files in the folder, reconciliation with broker balances, instruments and exchange rates. -->
<script lang="ts">
  import * as E from "../core/engine";
  import type { ImportReport } from "../data/types";
  import { app, need } from "../lib/state.svelte";
  import { api, loadAll, toast } from "../lib/actions";
  import { fmtDate, fmtNum } from "../lib/format";
  import ViewHead from "../components/ViewHead.svelte";
  import Card from "../components/Card.svelte";

  type Field = "name" | "yahoo" | "group";
  type Report = ImportReport | { file: string; error: string };

  const m = $derived(need(app.model));
  const d = $derived(need(app.data));
  const rec = $derived(E.reconcile(m));
  const bad = $derived(rec.filter((x) => !x.ok));
  const e = $derived(m.idx(m.today));
  const syms = $derived(Object.values(m.instr).filter((x) => m.txs.some((t) => t.symbol === x.symbol)).sort((a, b) => a.symbol.localeCompare(b.symbol)));
  const groups = $derived([...new Set(Object.values(m.instr).map((x) => x.group))]);
  const lastPt = (s: string) => { const pts = app.market?.prices?.[s]?.points; return pts && pts.length ? pts[pts.length - 1] : null; };
  const fxText = $derived(Object.entries(app.market?.fx || {}).map(([c, v]) => `${c}: ${v.points.length ? `${fmtNum(v.points[v.points.length - 1][1], 4)} zł (${fmtDate(v.points[v.points.length - 1][0])}, ${v.source === "nbp" ? "NBP tabela A" : v.source})` : "brak"}`).join(" · ") || "brak");

  // result of the last import (the component is not recreated by loadAll)
  let importing = $state(false);
  let reports = $state<Report[] | null>(null);
  let over = $state(false);
  let inp: HTMLInputElement;

  async function upload(files: File[]) {
    importing = true;
    reports = null;
    const out: Report[] = [];
    for (const f of files) {
      try {
        out.push(await api("/api/import", { method: "POST", headers: { "X-Filename": encodeURIComponent(f.name) }, body: await f.arrayBuffer() }));
      } catch (err) { out.push({ file: f.name, error: (err as Error).message }); }
    }
    try {
      await loadAll();
    } finally {
      importing = false;
      reports = out;
    }
    toast("Import zakończony");
  }
  function onDrop(ev: DragEvent) {
    ev.preventDefault();
    over = false;
    upload([...(ev.dataTransfer?.files || [])]);
  }

  // edited instrument fields (symbol → field → value)
  let edits = $state<Record<string, Partial<Record<Field, string>>>>({});
  const val = (sym: string, f: Field, def: string) => edits[sym]?.[f] ?? def;
  const edit = (sym: string, f: Field, v: string) => { edits[sym] = { ...(edits[sym] || {}), [f]: v }; };
  async function saveInstr(x: { symbol: string; name: string; yahoo: string; group: string }) {
    const body = { symbol: x.symbol, name: val(x.symbol, "name", x.name), yahoo: val(x.symbol, "yahoo", x.yahoo), group: val(x.symbol, "group", x.group) };
    const yahooChanged = body.yahoo !== (need(app.data).instruments[body.symbol]?.yahoo ?? body.symbol);
    await api("/api/instrument", { method: "POST", body: JSON.stringify(body) });
    toast(`Zapisano ${body.symbol}${yahooChanged ? " — pobieram notowania" : ""}`);
    delete edits[x.symbol];
    await loadAll({ refreshMarket: yahooChanged });
  }
</script>

<ViewHead title="Import i dane" sub="katalog danych: {d.dataDir}" />
<Card title="Dodaj pliki z transakcjami">
  <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
  <div class="dropzone" class:over id="drop" onclick={() => inp.click()} ondragover={(ev) => { ev.preventDefault(); over = true; }} ondragleave={() => (over = false)} ondrop={onDrop}><strong>Upuść pliki tutaj</strong> albo kliknij, aby wybrać<br /><span class="small">XTB: .xlsx lub .zip z xStation · IBKR: Activity Statement .csv · pliki we wspólnym formacie .csv</span>
    <input type="file" id="fileInp" multiple accept=".csv,.xlsx,.zip,.txt" hidden bind:this={inp} onchange={() => upload([...(inp.files || [])])} /></div>
  <div id="impResult">
    {#if importing}<p class="muted">Importowanie…</p>{:else if reports}
      {#each reports as r, i (i)}
        {#if "error" in r}<div class="notice">{r.file}: {r.error}</div>{:else}
          <p><b>{r.file}</b> ({r.kind.toUpperCase()}): {#each r.accounts as a, j (j)}{#if j}{"; "}{/if}{a.account} — nowych {a.new}, pominiętych duplikatów {a.duplicates}{#if a.file}{" "}→ <code>{a.file}</code>{/if}{#if a.new_instruments.length}, nowe instrumenty: {a.new_instruments.join(", ")}{/if}{/each}</p>
          {#if r.warnings.length}<div class="notice"><ul>{#each r.warnings as w, k (k)}<li>{w}</li>{/each}</ul></div>{/if}
        {/if}
      {/each}
    {/if}
  </div>
  <details class="small" style="margin-top:10px"><summary>Jak wyeksportować historię?</summary>
    <p><b>XTB</b> (xStation 5): Moje konto → Historia konta → wybierz rachunek i zakres „od początku” → Eksportuj (.xlsx). Możesz wgrać cały .zip z kilkoma rachunkami.</p>
    <p><b>IBKR</b>: Performance &amp; Reports → Statements → Activity → okres (np. rok, do 365 dni) → format CSV. Kilka raportów możesz wgrać osobno albo sklejone w jeden plik.</p>
    <p>Wgrywanie nakładających się okresów jest bezpieczne — transakcje o znanym <code>id</code> są pomijane. Specyfikacja formatu: <a href="FORMAT.md" target="_blank" rel="noopener">FORMAT.md</a>.</p>
  </details>
</Card>
<div class="grid g2">
  <Card title="Pliki w katalogu transactions">
    <div class="table-wrap"><table><thead><tr><th class="l">Plik</th><th>Wierszy</th><th>Duplikaty</th><th>Błędy</th><th class="l">Zmieniony</th></tr></thead><tbody>
      {#each d.files as f, i (i)}
        <tr><td class="l">{f.name}</td><td>{f.rows}</td><td>{f.duplicates || ""}</td><td>{#if f.errors}<span class="badge bad">{f.errors}</span>{/if}</td><td class="l">{f.modified.replace("T", " ")}</td></tr>
      {/each}
    </tbody></table></div>
    {#if d.errors?.length}<div class="notice" style="margin-top:10px"><b>Błędy w plikach</b><ul>{#each d.errors.slice(0, 50) as x, i (i)}<li>{x}</li>{/each}</ul></div>{/if}
  </Card>
  <Card title="Uzgodnienie ze stanami raportowanymi przez brokerów">
    {#if bad.length}<div class="notice">{bad.length} rozbieżności</div>{:else}<p><span class="badge ok">OK</span> Wszystkie {rec.length} pozycje i salda gotówki zgadzają się z raportami brokerów.</p>{/if}
    <div class="table-wrap"><table><thead><tr><th class="l">Data</th><th class="l">Rachunek</th><th class="l">Pozycja</th><th>Broker</th><th>Wyliczone</th><th></th></tr></thead><tbody>
      {#each rec as x, i (i)}
        <tr><td class="l">{fmtDate(x.date)}</td><td class="l">{x.account}</td><td class="l">{x.kind === "CASH" ? "gotówka " + x.currency : x.symbol}</td><td>{fmtNum(x.reported, x.kind === "CASH" ? 2 : 4)}</td><td>{fmtNum(x.computed, x.kind === "CASH" ? 2 : 4)}</td><td>{#if x.ok}<span class="badge ok">OK</span>{:else}<span class="badge bad">różnica</span>{/if}</td></tr>
      {/each}
    </tbody></table></div>
  </Card>
</div>
<Card title="Instrumenty i notowania">
  <p class="small muted">Symbol Yahoo służy do pobierania notowań (np. <code>EUNL.DE</code>, <code>IWDA.AS</code>, <code>IGLN.L</code>, <code>CDR.WA</code>, <code>AAPL</code>). Grupa decyduje o alokacji i rebalansowaniu. Zmiany zapisują się w <code>data/instruments.csv</code>.</p>
  <div class="table-wrap"><table><thead><tr><th class="l">Symbol</th><th class="l">Nazwa</th><th class="l">ISIN</th><th class="l">Symbol Yahoo</th><th class="l">Grupa</th><th class="l">Źródło notowań</th><th>Ostatni kurs</th><th></th></tr></thead><tbody>
    {#each syms as x (x.symbol)}
      {@const lp = lastPt(x.symbol)}
      {@const src = m.priceSource[x.symbol] || "none"}
      <tr data-sym={x.symbol}><td class="l"><b>{x.symbol}</b></td><td class="l"><input type="text" data-f="name" value={val(x.symbol, "name", x.name)} oninput={(ev) => edit(x.symbol, "name", ev.currentTarget.value)} style="width:220px" /></td><td class="l">{x.isin}</td>
        <td class="l"><input type="text" data-f="yahoo" value={val(x.symbol, "yahoo", x.yahoo)} oninput={(ev) => edit(x.symbol, "yahoo", ev.currentTarget.value)} style="width:100px" /></td><td class="l"><input type="text" data-f="group" value={val(x.symbol, "group", x.group)} oninput={(ev) => edit(x.symbol, "group", ev.currentTarget.value)} list="groupList" style="width:190px" /></td>
        <td class="l">{#if src === "yahoo" || src === "stooq" || src === "market" || src === "manual"}<span class="badge ok">{src}</span>{:else if src.startsWith("sibling")}<span class="badge warn">z {src.split(":")[1]}</span>{:else}<span class="badge bad">{src === "fallback" ? "tylko ceny z transakcji" : "brak"}</span>{/if}</td>
        <td>{#if lp}{fmtNum(lp[1], 3)} {m.priceCcy[x.symbol]} <span class="muted small">{fmtDate(lp[0])}</span>{:else if isNaN(m.priceArr[x.symbol]?.[e] as number)}–{:else}{fmtNum(m.priceArr[x.symbol][e], 3)} {m.priceCcy[x.symbol]}{/if}</td>
        <td><button class="btn saveInstr" onclick={() => saveInstr(x)}>Zapisz</button></td></tr>
    {/each}
  </tbody></table><datalist id="groupList">{#each groups as g (g)}<option value={g}></option>{/each}</datalist></div>
</Card>
<Card title="Kursy walut"><p class="small">{fxText}</p></Card>
